import React, {lazy, Suspense} from 'react';
import {BrowserRouter as Router, Routes, Route, Navigate} from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import {SocketProvider, useSocket} from './context/SocketContext';
import { ThemeProvider } from './context/ThemeContext';
import './App.css';
import GlobalSocketHandler from "./GlobalSocketHandler";
import ReconnectionBanner from "./components/Reconnectionbanner";

// Lazy load components
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const VerifyEmail = lazy(() => import('./components/VerifyEmail'));
const ResendVerify = lazy(() => import('./components/ResendVerify'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const Chat = lazy(() => import('./components/Chat'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const UserManagement = lazy(() => import('./components/admin/UserManagement/UserManagement'));
const UserProfile = lazy(() => import('./components/UserProfile'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));

// Loading component
const LoadingSpinner = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
    }}>
        <div>読み込み中…</div>
    </div>
);

// Protected Route component
const ProtectedRoute: React.FC<{
    children: React.ReactNode;
    adminOnly?: boolean;
    userOnly?: boolean; // Thêm option này
}> = ({ children, adminOnly = false, userOnly = false }) => {
    const { isAuthenticated, user, isLoading } = useAuth();

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (userOnly && (user?.role === 'admin' || user?.role === 'super_admin')) {
        return <Navigate to="/admin/dashboard" replace />;
    }

    if (adminOnly && user?.role === 'user') {
        return <Navigate to="/chat" replace />;
    }

    return <>{children}</>;
};

// Public Route component (redirect if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, user, isLoading } = useAuth();

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (isAuthenticated) {
        return <Navigate to={user?.role === 'admin' || user?.role === 'super_admin' ? '/admin/dashboard' : '/chat'} replace />;
    }

    return <>{children}</>;
};

const AdminRoutes = () => {
    return (
        <Suspense fallback={<LoadingSpinner />}>
            <AdminLayout>
                <Routes>
                    <Route path="/dashboard/*" element={<AdminDashboard />} />
                    <Route path="/users" element={<UserManagement />} />
                </Routes>
            </AdminLayout>
        </Suspense>
    );
};

const AppContent: React.FC = () => {
    const { reconnectionFailed, manualReconnect } = useSocket();

    return (
        <>
            {reconnectionFailed && <ReconnectionBanner onReconnect={manualReconnect} />}
        </>
    );
};

function App() {
    console.log('Version: 4.x.x');

    return (
        <ThemeProvider>
            <AuthProvider>
                <Router>
                    <SocketProvider>
                        <GlobalSocketHandler />
                        <div className="App">
                            <Suspense fallback={<LoadingSpinner />}>
                                <AppContent />
                                <Routes>
                                    {/* Public Routes */}
                                    <Route
                                        path="/login"
                                        element={
                                            <PublicRoute>
                                                <Login />
                                            </PublicRoute>
                                        }
                                    />
                                    <Route
                                        path="/register"
                                        element={
                                            <PublicRoute>
                                                <Register />
                                            </PublicRoute>
                                        }
                                    />
                                    <Route
                                        path="/resend-verification"
                                        element={
                                            <PublicRoute>
                                                <ResendVerify />
                                            </PublicRoute>
                                        }
                                    />
                                    <Route
                                        path="/verify-email/:token"
                                        element={
                                            <PublicRoute>
                                                <VerifyEmail />
                                            </PublicRoute>
                                        }
                                    />
                                    <Route
                                        path="/forgot-password"
                                        element={
                                            <PublicRoute>
                                                <ForgotPassword />
                                            </PublicRoute>
                                        }
                                    />
                                    <Route
                                        path="/reset-password/:token"
                                        element={
                                            <PublicRoute>
                                                <ResetPassword />
                                            </PublicRoute>
                                        }
                                    />

                                    {/* Protected Routes */}
                                    <Route
                                        path="/chat"
                                        element={
                                            <ProtectedRoute userOnly={true}>
                                                <Chat />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route
                                        path="/profile"
                                        element={
                                            <ProtectedRoute>
                                                <UserProfile />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/admin/*"
                                        element={
                                            <ProtectedRoute adminOnly={true}>
                                                <AdminRoutes />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Default redirect */}
                                    <Route path="/" element={<Navigate to="/login" replace />} />
                                    <Route path="*" element={<Navigate to="/login" replace />} />
                                </Routes>
                            </Suspense>
                        </div>
                    </SocketProvider>
                </Router>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;